import React from 'react';
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { cn } from '../../ui/utils';
import type {
  PerformanceCompanyGoal,
  PerformanceDepartmentGoal,
  PerformanceScheduleDetail,
} from '../../../types/performance.types';
import { performanceApi } from '../../../utils/performance-api';
import { PerformancePill } from '../PerformancePrimitives';

type SubTab = 'company' | 'department' | 'setup';

interface GoalsTabProps {
  detail: PerformanceScheduleDetail;
  users: { id: string | number; name: string }[];
  departments: { id: string | number; departmentName: string; departmentCode?: string }[];
  onChanged: () => Promise<void> | void;
}

export const GoalsTab: React.FC<GoalsTabProps> = ({
  detail,
  users,
  departments,
  onChanged,
}) => {
  const [subTab, setSubTab] = React.useState<SubTab>('company');
  const schedule = detail.schedule;
  const readOnly = schedule.status === 'Closed';

  const [companyDialog, setCompanyDialog] = React.useState<{
    open: boolean;
    goal?: PerformanceCompanyGoal | null;
  }>({ open: false });
  const [departmentDialog, setDepartmentDialog] = React.useState<{
    open: boolean;
    goal?: PerformanceDepartmentGoal | null;
  }>({ open: false });
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [ownerId, setOwnerId] = React.useState('none');
  const [departmentId, setDepartmentId] = React.useState('');
  const [companyGoalId, setCompanyGoalId] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [newCategory, setNewCategory] = React.useState('');

  const [setup, setSetup] = React.useState({
    goalOpenDate: schedule.goalOpenDate,
    goalCloseDate: schedule.goalCloseDate,
    minGoals: schedule.minGoals,
    maxGoals: schedule.maxGoals,
    minWeight: schedule.minWeight,
    maxWeight: schedule.maxWeight,
  });
  const [savingSetup, setSavingSetup] = React.useState(false);

  React.useEffect(() => {
    setSetup({
      goalOpenDate: schedule.goalOpenDate,
      goalCloseDate: schedule.goalCloseDate,
      minGoals: schedule.minGoals,
      maxGoals: schedule.maxGoals,
      minWeight: schedule.minWeight,
      maxWeight: schedule.maxWeight,
    });
  }, [schedule]);

  const openCompany = (goal?: PerformanceCompanyGoal | null) => {
    setTitle(goal?.title || '');
    setDescription(goal?.description || '');
    setOwnerId(goal?.ownerId ? String(goal.ownerId) : 'none');
    setCompanyDialog({ open: true, goal });
  };

  const openDepartment = (goal?: PerformanceDepartmentGoal | null) => {
    setTitle(goal?.title || '');
    setDescription(goal?.description || '');
    setOwnerId(goal?.ownerId ? String(goal.ownerId) : 'none');
    setDepartmentId(goal?.departmentId ? String(goal.departmentId) : '');
    setCompanyGoalId(goal?.companyGoalId ? String(goal.companyGoalId) : '');
    setDepartmentDialog({ open: true, goal });
  };

  const saveCompany = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        ownerId: ownerId === 'none' ? null : Number(ownerId),
      };
      if (companyDialog.goal) {
        await performanceApi.cascade.updateCompany(
          schedule.id,
          companyDialog.goal.id,
          payload
        );
      } else {
        await performanceApi.cascade.createCompany(schedule.id, payload);
      }
      setCompanyDialog({ open: false });
      toast.success(companyDialog.goal ? 'Company goal updated' : 'Company goal created');
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const saveDepartment = async () => {
    if (!title.trim() || !departmentId || !companyGoalId) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        ownerId: ownerId === 'none' ? null : Number(ownerId),
        departmentId: Number(departmentId),
        companyGoalId: Number(companyGoalId),
      };
      if (departmentDialog.goal) {
        await performanceApi.cascade.updateDepartment(
          schedule.id,
          departmentDialog.goal.id,
          payload
        );
      } else {
        await performanceApi.cascade.createDepartment(schedule.id, payload);
      }
      setDepartmentDialog({ open: false });
      toast.success(
        departmentDialog.goal ? 'Department goal updated' : 'Department goal created'
      );
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const removeCompany = async (goal: PerformanceCompanyGoal) => {
    setSaving(true);
    try {
      await performanceApi.cascade.deleteCompany(schedule.id, goal.id);
      toast.success('Company goal deleted');
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const removeDepartment = async (goal: PerformanceDepartmentGoal) => {
    setSaving(true);
    try {
      await performanceApi.cascade.deleteDepartment(schedule.id, goal.id);
      toast.success('Department goal deleted');
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const saveSetup = async () => {
    setSavingSetup(true);
    try {
      await performanceApi.schedules.update(schedule.id, setup);
      toast.success('Goal-setting rules saved');
      await onChanged();
    } finally {
      setSavingSetup(false);
    }
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    await performanceApi.settings.createCategory(schedule.id, { name: newCategory.trim() });
    setNewCategory('');
    toast.success('Category added');
    await onChanged();
  };

  const updateCategory = async (categoryId: number, patch: Record<string, unknown>) => {
    await performanceApi.settings.updateCategory(schedule.id, categoryId, patch);
    await onChanged();
  };

  const moveCategory = async (index: number, direction: -1 | 1) => {
    const ordered = [...detail.categories].sort((a, b) => a.sortOrder - b.sortOrder);
    const target = ordered[index + direction];
    const current = ordered[index];
    if (!target || !current) return;
    await performanceApi.settings.updateCategory(schedule.id, current.id, {
      sortOrder: target.sortOrder,
    });
    await performanceApi.settings.updateCategory(schedule.id, target.id, {
      sortOrder: current.sortOrder,
    });
    await onChanged();
  };

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-full bg-muted p-1">
        {(
          [
            ['company', 'Company Goals'],
            ['department', 'Department Goals'],
            ['setup', 'My Goals Setup'],
          ] as [SubTab, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setSubTab(value)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              subTab === value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {subTab === 'company' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Company goals are the top of the cascade. Every department goal must feed one.
            </p>
            {!readOnly ? (
              <Button size="sm" onClick={() => openCompany()}>
                <Plus className="mr-1.5 h-4 w-4" /> New company goal
              </Button>
            ) : null}
          </div>
          <div className="space-y-2">
            {detail.companyGoals.map((goal, index) => (
              <div key={goal.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-muted-foreground">
                        C{index + 1}
                      </span>
                      <p className="text-sm font-semibold">{goal.title}</p>
                    </div>
                    {goal.description ? (
                      <p className="mt-1 text-sm text-muted-foreground">{goal.description}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {goal.ownerName ? `Owner ${goal.ownerName}` : 'No owner'} ·{' '}
                      {goal.departmentGoalCount || 0} department goals
                    </p>
                  </div>
                  {!readOnly ? (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openCompany(goal)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCompany(goal)}
                        disabled={saving}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {!detail.companyGoals.length ? (
              <p className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No company goals yet.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {subTab === 'department' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Department goals are visible to everyone in that department and can be aligned to
              from My Performance.
            </p>
            {!readOnly ? (
              <Button size="sm" onClick={() => openDepartment()}>
                <Plus className="mr-1.5 h-4 w-4" /> New department goal
              </Button>
            ) : null}
          </div>
          <div className="space-y-2">
            {detail.departmentGoals.map((goal) => (
              <div key={goal.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <PerformancePill tone="muted">
                        {goal.departmentName || 'Department'}
                        {goal.departmentCode ? ` (${goal.departmentCode})` : ''}
                      </PerformancePill>
                      <p className="text-sm font-semibold">{goal.title}</p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      ↳ {goal.companyGoalTitle}
                      {goal.ownerName ? ` · Owner ${goal.ownerName}` : ''}
                    </p>
                  </div>
                  {!readOnly ? (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openDepartment(goal)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDepartment(goal)}
                        disabled={saving}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {!detail.departmentGoals.length ? (
              <p className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No department goals yet.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {subTab === 'setup' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold">Goal-setting window</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Employees can create and edit goals (and weights) only inside this window, or
              after HR unlocks their plan.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="setup-open">Opens</Label>
                <Input
                  id="setup-open"
                  type="date"
                  value={setup.goalOpenDate}
                  min={schedule.startDate}
                  max={schedule.endDate}
                  onChange={(event) =>
                    setSetup((current) => ({ ...current, goalOpenDate: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="setup-close">Closes</Label>
                <Input
                  id="setup-close"
                  type="date"
                  value={setup.goalCloseDate}
                  min={setup.goalOpenDate}
                  max={schedule.endDate}
                  onChange={(event) =>
                    setSetup((current) => ({ ...current, goalCloseDate: event.target.value }))
                  }
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(
                [
                  ['minGoals', 'Min goals'],
                  ['maxGoals', 'Max goals'],
                  ['minWeight', 'Min weight %'],
                  ['maxWeight', 'Max weight %'],
                ] as const
              ).map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`setup-${key}`}>{label}</Label>
                  <Input
                    id={`setup-${key}`}
                    type="number"
                    value={setup[key]}
                    onChange={(event) =>
                      setSetup((current) => ({
                        ...current,
                        [key]: Number(event.target.value),
                      }))
                    }
                  />
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Employees can set goals {setup.goalOpenDate} – {setup.goalCloseDate}.
            </p>
            <Button className="mt-3" size="sm" onClick={saveSetup} disabled={savingSetup || readOnly}>
              {savingSetup ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save rules
            </Button>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold">Goal categories</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Archiving hides a category from the picker; goals already using it keep their colour.
            </p>
            <div className="mt-3 space-y-2">
              {[...detail.categories]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((category, index, ordered) => (
                  <div
                    key={category.id}
                    className="flex flex-wrap items-center gap-2 rounded-[10px] border border-border px-3 py-2"
                  >
                    <input
                      type="color"
                      defaultValue={category.colour}
                      disabled={readOnly}
                      aria-label={`Colour for ${category.name}`}
                      onChange={(event) =>
                        updateCategory(category.id, { colour: event.target.value })
                      }
                      className="h-6 w-6 cursor-pointer rounded border border-border bg-transparent p-0"
                    />
                    <Input
                      defaultValue={category.name}
                      disabled={readOnly}
                      className={cn(
                        'h-7 max-w-[240px] text-sm',
                        !category.active && 'text-muted-foreground line-through'
                      )}
                      onBlur={(event) => {
                        const name = event.target.value.trim();
                        if (name && name !== category.name) {
                          updateCategory(category.id, { name });
                        }
                      }}
                    />
                    <div className="ml-auto flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={readOnly || index === 0}
                        onClick={() => moveCategory(index, -1)}
                        aria-label={`Move ${category.name} up`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={readOnly || index === ordered.length - 1}
                        onClick={() => moveCategory(index, 1)}
                        aria-label={`Move ${category.name} down`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateCategory(category.id, { active: !category.active })}
                      >
                        {category.active ? 'Archive' : 'Restore'}
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input
                value={newCategory}
                onChange={(event) => setNewCategory(event.target.value)}
                placeholder="New category name"
                className="max-w-xs"
              />
              <Button variant="outline" onClick={addCategory} disabled={!newCategory.trim()}>
                <Plus className="mr-1.5 h-4 w-4" /> Add
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Dialog
        open={companyDialog.open}
        onOpenChange={(open) => setCompanyDialog((current) => ({ ...current, open }))}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {companyDialog.goal ? 'Edit company goal' : 'New company goal'}
            </DialogTitle>
            <DialogDescription>
              Department goals must feed this. Individuals see it read-only in My Performance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="company-title">Title</Label>
              <Input
                id="company-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-desc">Description</Label>
              <Textarea
                id="company-desc"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No owner</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCompanyDialog((current) => ({ ...current, open: false }))}
            >
              Cancel
            </Button>
            <Button onClick={saveCompany} disabled={saving || !title.trim()}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={departmentDialog.open}
        onOpenChange={(open) => setDepartmentDialog((current) => ({ ...current, open }))}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {departmentDialog.goal ? 'Edit department goal' : 'New department goal'}
            </DialogTitle>
            <DialogDescription>
              Align it to a company goal. Department members can cascade their goals from it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="dept-title">Title</Label>
              <Input
                id="dept-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dept-desc">Description</Label>
              <Textarea
                id="dept-desc"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={String(department.id)}>
                      {department.departmentName}
                      {department.departmentCode ? ` (${department.departmentCode})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Feeds company goal</Label>
              <Select value={companyGoalId} onValueChange={setCompanyGoalId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a company goal" />
                </SelectTrigger>
                <SelectContent>
                  {detail.companyGoals.map((goal) => (
                    <SelectItem key={goal.id} value={String(goal.id)}>
                      {goal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No owner</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDepartmentDialog((current) => ({ ...current, open: false }))}
            >
              Cancel
            </Button>
            <Button
              onClick={saveDepartment}
              disabled={saving || !title.trim() || !departmentId || !companyGoalId}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
